/**
 * Simplified overhead of one race: the course rotated so its axis points up, one track per boat,
 * T / G / R / B letters at maneuvers (the map's single-letter style), and three colourings:
 *   boat    team colour (source catalog)
 *   wind    the explore map's WIND encoding: colour = TWD against the race mean on red -> lightgrey ->
 *           green over mean +- 1 std (green = veered, right of the mean; red = backed, left of it),
 *           line width = TWS over its own +- 1 std band
 *   dtl     change in distance to leader: red = gaining, grey = holding, blue = losing
 * While the cursor is set (hovering the distance chart or the plot) only the last minute of each
 * track is drawn, ending at a boat shape pointing along its course. With `leg` set each boat is drawn
 * only while it sails that leg.
 * Plain SVG drawn with d3 (a schematic, not a map: no basemap, no Mapbox layers).
 */
import { createEffect, createMemo, For, Show, onCleanup } from "solid-js";
import * as d3 from "d3";
import { chartInk } from "../../../utils/chartInk";
import { isDark } from "../../../store/themeStore";
import { sourcesStore } from "../../../store/sourcesStore";
import { convertSpeedMagnitude, speedUnitBracketUpper, speedUnitShortLabel, type SpeedDisplayUnit } from "../../../utils/speedUnits";
import { dtlChange, formatRaceClock, raceSecondsAt, type RaceTimelineMark, type RaceTimelineRace } from "./raceTimeline";

export type CourseColorMode = "boat" | "wind" | "dtl";

const WIDTH = 640;
const HEIGHT = 560;
const PAD = 28;
const EARTH_R = 6371000;
/** Trail drawn behind each boat while the cursor is set. */
const TRAIL_S = 60;
/** Line width range for TWS in wind mode (px at the plot's viewBox scale). */
const WIND_WIDTH_MIN = 0.8;
const WIND_WIDTH_MAX = 4.5;
/** Boat outline pointing up (bow at -y), ~17 px long. */
const BOAT_PATH = "M0,-9 C3.6,-4 4,2 3,8 L-3,8 C-4,2 -3.6,-4 0,-9 Z";
/** Boat name offset from the boat, in screen px (as on the prestart map). */
const BOAT_LABEL_DX = 12;
const BOAT_LABEL_DY = -8;

/** [mean - std, mean, mean + std] (sample deviation), or null with no values. */
function oneSigma(values: number[]): [number, number, number] | null {
  if (values.length === 0) {return null;}
  const mean = d3.mean(values) as number;
  const std = values.length > 1 ? (d3.deviation(values) as number) : 0;
  return [mean - std, mean, mean + std];
}

interface Props {
  race: RaceTimelineRace;
  sources: string[];
  mode: CourseColorMode;
  speedUnit: SpeedDisplayUnit;
  /** Grid index set from the distance chart; the map shows the last minute up to it. */
  cursor: number | null;
  /** Draw only the seconds each boat spends on this leg (null = the whole race). */
  leg?: number | null;
  /**
   * Markwind / marks timesteps for this race (seconds from t0), same series the fleet map animates.
   * When set, the course view prefers these over race_timeline course_changes so marks move with the cursor.
   */
  markCourseChanges?: { t: number; marks: RaceTimelineMark[] }[];
}

/** Resolved value of a CSS custom property on :root (d3 interpolation needs real colours). */
function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export default function RaceCourseView(props: Props) {
  let svgRef: SVGSVGElement | undefined;

  /** Course frame: x = metres right of the axis, y = metres up the axis, from the bottom gate. */
  const frame = createMemo(() => {
    const race = props.race;
    const c = race.course;
    const axis = c.axis_deg ?? c.legs[0]?.axis_deg ?? 0;
    let origin = c.bottom ?? c.start;
    if (!origin) {
      for (const b of Object.values(race.boats)) {
        const i = b.lat.findIndex((v) => v != null);
        if (i >= 0) {origin = [b.lat[i] as number, b.lon[i] as number]; break;}
      }
    }
    const o = origin ?? [0, 0];
    const r = (axis * Math.PI) / 180;
    const cosLat = Math.cos((o[0] * Math.PI) / 180);
    const toXY = (lat: number, lon: number): [number, number] => {
      const n = ((lat - o[0]) * Math.PI / 180) * EARTH_R;
      const e = ((lon - o[1]) * Math.PI / 180) * EARTH_R * cosLat;
      return [e * Math.cos(r) - n * Math.sin(r), e * Math.sin(r) + n * Math.cos(r)];
    };
    const pts: Record<string, ([number, number] | null)[]> = {};
    const all: [number, number][] = [];
    const leg = props.leg ?? null;
    for (const s of props.sources) {
      const b = race.boats[s];
      if (!b) {continue;}
      pts[s] = b.lat.map((lat, i) => {
        const lon = b.lon[i];
        if (lat == null || lon == null) {return null;}
        if (leg != null && b.leg[i] !== leg) {return null;}
        const p = toXY(lat, lon);
        all.push(p);
        return p;
      });
    }
    const project = (ms: RaceTimelineMark[]) => ms.map((m) => ({ ...m, xy: toXY(m.lat, m.lon) }));
    // Prefer markwind (map animation source); fall back to race_timeline re-lay snapshots.
    const rawChanges =
      (props.markCourseChanges && props.markCourseChanges.length > 0)
        ? props.markCourseChanges
        : (race.course_changes ?? []);
    const fromMarkwind = !!(props.markCourseChanges && props.markCourseChanges.length > 0);
    const courses = rawChanges.map((cc) => ({ t: cc.t, marks: project(cc.marks) }));
    const marks = courses.length > 0 ? courses[0].marks : project(c.marks);
    marks.forEach((m) => all.push(m.xy));
    // Bound the view from a sample of layouts (not every 10 s markwind frame — that bloated the extent).
    const sample = courses.length <= 8
      ? courses
      : [courses[0], courses[Math.floor(courses.length / 2)], courses[courses.length - 1]];
    sample.forEach((cc) => cc.marks.forEach((m) => all.push(m.xy)));
    const xs = all.map((p) => p[0]);
    const ys = all.map((p) => p[1]);
    const [x0, x1] = [d3.min(xs) ?? -100, d3.max(xs) ?? 100];
    const [y0, y1] = [d3.min(ys) ?? -100, d3.max(ys) ?? 100];
    // One scale for both axes so the course is not distorted.
    const k = Math.min((WIDTH - 2 * PAD) / Math.max(x1 - x0, 1), (HEIGHT - 2 * PAD) / Math.max(y1 - y0, 1));
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    const sx = (x: number) => WIDTH / 2 + (x - cx) * k;
    const sy = (y: number) => HEIGHT / 2 - (y - cy) * k;
    return {
      pts,
      marks,
      courses,
      fromMarkwind,
      sx,
      sy,
      k,
      axis,
      hasCourse: marks.length > 0 || c.marks.length > 0,
    };
  });

  const colorScales = createMemo(() => {
    const dark = isDark();
    const race = props.race;
    const tws: number[] = [];
    const change: number[] = [];
    let sin = 0;
    let cos = 0;
    for (const s of props.sources) {
      const b = race.boats[s];
      if (!b) {continue;}
      b.tws_kts.forEach((v) => { if (v != null) {tws.push(v);} });
      b.twd_deg.forEach((v) => {
        if (v != null) {sin += Math.sin((v * Math.PI) / 180); cos += Math.cos((v * Math.PI) / 180);}
      });
      dtlChange(b.lfl_m ?? b.dtl_m).forEach((v) => { if (v != null) {change.push(Math.abs(v));} });
    }
    // TWD as a shift from its circular mean, so a race sailed in a northerly is not split at 0 / 360.
    const twdMean = ((Math.atan2(sin, cos) * 180) / Math.PI + 360) % 360;
    const shiftOf = (v: number) => ((((v - twdMean) % 360) + 540) % 360) - 180;
    const shifts: number[] = [];
    for (const s of props.sources) {
      race.boats[s]?.twd_deg.forEach((v) => { if (v != null) {shifts.push(shiftOf(v));} });
    }
    const twdBand = oneSigma(shifts) ?? [-1, 0, 1];
    const twsBand = oneSigma(tws) ?? [0, 1, 1];
    const lim = Math.max(d3.quantile(change.sort(d3.ascending), 0.95) ?? 1, 1);
    const gain = cssVar("--color-race-gain", "#dc2626");
    const loss = cssVar("--color-race-loss", "#2563eb");
    const mid = cssVar("--color-race-neutral", dark ? "#6b7280" : "#a3a3a3");
    const twdScale = d3.scaleLinear<string>()
      .domain([twdBand[0], twdBand[1], Math.max(twdBand[2], twdBand[0] + 1)])
      .range([
        cssVar("--color-race-wind-left", "red"),
        cssVar("--color-race-wind-mid", "lightgrey"),
        cssVar("--color-race-wind-right", "green"),
      ])
      .clamp(true);
    const twsScale = d3.scaleLinear()
      .domain([twsBand[0], Math.max(twsBand[2], twsBand[0] + 0.1)])
      .range([WIND_WIDTH_MIN, WIND_WIDTH_MAX])
      .clamp(true);
    return {
      /** TWD colour: red = left of (backed from) the race mean, green = right of it (veered). */
      windColor: (twd: number) => twdScale(shiftOf(twd)),
      windWidth: (twsKts: number) => twsScale(twsKts),
      twdMean,
      /** Half the mean +- 1 std band of the TWD shift, degrees. */
      twdSpread: (twdBand[2] - twdBand[0]) / 2,
      twsBand,
      // Negative change = closing on the leader = gain (red).
      dtl: d3.scaleLinear<string>().domain([-lim, 0, lim]).range([gain, mid, loss]).clamp(true),
      dtlLimit: lim,
    };
  });

  /** One boat's track from sample `from` to `to` in the current colouring, appended to `parent`. */
  const drawSegments = (parent: d3.Selection<SVGGElement, unknown, null, undefined>, s: string, from: number, to: number) => {
    const f = frame();
    const pts = f.pts[s];
    const b = props.race.boats[s];
    if (!pts || !b) {return;}
    const ink = chartInk(isDark());
    const scales = colorScales();
    const lo = Math.max(0, from);
    const hi = Math.min(pts.length - 1, to);
    if (props.mode === "boat") {
      const line = d3.line<[number, number] | null>()
        .defined((p) => p != null)
        .x((p) => f.sx((p as [number, number])[0]))
        .y((p) => f.sy((p as [number, number])[1]));
      parent.append("path").attr("d", line(pts.slice(lo, hi + 1))).attr("fill", "none")
        .attr("stroke", sourcesStore.getSourceColor(s) || ink.axis).attr("stroke-width", 2)
        .attr("stroke-linejoin", "round");
      return;
    }
    const change = props.mode === "dtl" ? dtlChange(b.lfl_m ?? b.dtl_m) : null;
    const segs: { a: [number, number]; b: [number, number]; c: string; w: number }[] = [];
    for (let i = lo + 1; i <= hi; i++) {
      const p0 = pts[i - 1];
      const p1 = pts[i];
      if (!p0 || !p1) {continue;}
      if (props.mode === "wind") {
        const twd = b.twd_deg[i];
        const tws = b.tws_kts[i];
        segs.push({
          a: p0, b: p1,
          c: twd == null ? ink.grid : scales.windColor(twd),
          w: tws == null ? 1 : scales.windWidth(tws),
        });
      } else {
        const v = change?.[i];
        segs.push({ a: p0, b: p1, c: v == null ? ink.grid : scales.dtl(v), w: 2.5 });
      }
    }
    parent.append("g").selectAll("line").data(segs).join("line")
      .attr("x1", (d) => f.sx(d.a[0])).attr("y1", (d) => f.sy(d.a[1]))
      .attr("x2", (d) => f.sx(d.b[0])).attr("y2", (d) => f.sy(d.b[1]))
      .attr("stroke", (d) => d.c).attr("stroke-width", (d) => d.w).attr("stroke-linecap", "round");
  };

  /** One course's marks into `parent`: gate / line pairs dashed, rings, names outside each pair. */
  const drawCourseMarks = (parent: d3.Selection<SVGGElement, unknown, null, undefined>,
    marks: ReturnType<typeof frame>["marks"]) => {
    const ink = chartInk(isDark());
    const f = frame();
    const byPos = d3.group(marks, (m) => m.position);
    for (const [, ms] of byPos) {
      if (ms.length === 2) {
        parent.append("line")
          .attr("x1", f.sx(ms[0].xy[0])).attr("y1", f.sy(ms[0].xy[1]))
          .attr("x2", f.sx(ms[1].xy[0])).attr("y2", f.sy(ms[1].xy[1]))
          .attr("stroke", ink.grid).attr("stroke-width", 1.5).attr("stroke-dasharray", "4 3");
      }
    }
    parent.selectAll("circle.mark").data(marks).join("circle")
      .attr("class", "mark")
      .attr("cx", (m) => f.sx(m.xy[0])).attr("cy", (m) => f.sy(m.xy[1])).attr("r", 5)
      .attr("fill", "none").attr("stroke", ink.axis).attr("stroke-width", 1.5);
    // Labels on the outside of each gate so a pair's names never overlap.
    const leftOfPair = new Set<string>();
    for (const [, ms] of byPos) {
      if (ms.length === 2) {leftOfPair.add(ms[0].xy[0] <= ms[1].xy[0] ? ms[0].name : ms[1].name);}
    }
    parent.selectAll("text.mark-label").data(marks).join("text")
      .attr("class", "mark-label")
      .attr("x", (m) => f.sx(m.xy[0]) + (leftOfPair.has(m.name) ? -8 : 8)).attr("y", (m) => f.sy(m.xy[1]) + 4)
      .attr("text-anchor", (m) => (leftOfPair.has(m.name) ? "end" : "start"))
      .attr("fill", ink.label).attr("font-size", 10).text((m) => m.name);
  };

  /** The course in force at grid sample `i`: the last change at or before it (else the first). */
  const courseAt = (i: number) => {
    const f = frame();
    const t = i * (props.race.dt || 1);
    let pick = f.courses[0];
    for (const cc of f.courses) {if (cc.t <= t) {pick = cc;}}
    return pick;
  };

  const draw = () => {
    if (!svgRef) {return;}
    const ink = chartInk(isDark());
    const f = frame();
    const svg = d3.select(svgRef);
    svg.selectAll("*").remove();
    svg.attr("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);

    // Course: start line / gates as dashed lines, marks as rings with names.
    // Markwind (dense): idle = gun layout only; cursor animates like the fleet map.
    // Sparse re-lays from race_timeline: idle overlays every layout the race used.
    const g = svg.append("g").attr("class", "race-course-marks");
    if (f.courses.length > 1 && !f.fromMarkwind) {
      for (const cc of f.courses) {drawCourseMarks(g.append("g"), cc.marks);}
    } else {
      drawCourseMarks(g, f.marks);
    }

    // Wind arrow: points the way the wind flows, down the page (the course axis is up the page);
    // the label is the axis bearing, where the wind comes from (user, 2026-10-13).
    const arrow = svg.append("g").attr("transform", `translate(${WIDTH - 22}, 34)`);
    arrow.append("line").attr("x1", 0).attr("y1", -14).attr("x2", 0).attr("y2", 10)
      .attr("stroke", ink.axis).attr("stroke-width", 1.5);
    arrow.append("path").attr("d", "M-5,6 L0,14 L5,6 Z").attr("fill", ink.axis);
    arrow.append("text").attr("y", 32).attr("text-anchor", "middle").attr("font-size", 10)
      .attr("fill", ink.label).text(`${Math.round(f.axis)}°`);

    // Tracks (hidden while the cursor shows only the last minute -- see drawCursor).
    const tracks = svg.append("g").attr("class", "race-course-tracks");
    for (const s of props.sources) {
      const pts = f.pts[s];
      const b = props.race.boats[s];
      if (!pts || !b) {continue;}
      const team = sourcesStore.getSourceColor(s) || ink.axis;
      drawSegments(tracks, s, 0, pts.length - 1);
      // Maneuver letters: team-coloured dot carries identity, the letter wears ink.
      const hits = b.mnvr.map((m, i) => (m && pts[i] ? { m, p: pts[i] as [number, number] } : null))
        .filter((d): d is { m: string; p: [number, number] } => d != null);
      const mg = tracks.append("g");
      mg.selectAll("circle").data(hits).join("circle")
        .attr("cx", (d) => f.sx(d.p[0])).attr("cy", (d) => f.sy(d.p[1])).attr("r", 6)
        .attr("fill", isDark() ? "#111" : "#fff").attr("stroke", team).attr("stroke-width", 1.5);
      mg.selectAll("text").data(hits).join("text")
        .attr("x", (d) => f.sx(d.p[0])).attr("y", (d) => f.sy(d.p[1]) + 3)
        .attr("text-anchor", "middle").attr("font-size", 8).attr("font-weight", 700)
        .attr("fill", ink.label).text((d) => d.m);
    }

    // The map only follows the cursor (set from the distance chart); hovering the map itself does
    // nothing (user, 2026-10-09).
    svg.append("g").attr("class", "race-course-cursor");
    drawCursor();
  };

  const drawCursor = () => {
    if (!svgRef) {return;}
    const root = d3.select(svgRef);
    const layer = root.select<SVGGElement>("g.race-course-cursor");
    layer.selectAll("*").remove();
    const i = props.cursor;
    // Full tracks when idle; while scrubbing only the last minute behind each boat.
    root.select("g.race-course-tracks").attr("display", i == null ? null : "none");
    // Marks follow the cursor whenever we have timed layouts (markwind or re-lays).
    const f = frame();
    const timed = f.courses.length > 1;
    root.select("g.race-course-marks").attr("display", i != null && timed ? "none" : null);
    if (i == null) {return;}
    if (timed) {drawCourseMarks(layer.append("g"), courseAt(i).marks);}
    const ink = chartInk(isDark());
    const back = Math.round(TRAIL_S / (props.race.dt || 1));
    for (const s of props.sources) {drawSegments(layer, s, i - back, i);}
    for (const s of props.sources) {
      const pts = f.pts[s];
      const p = pts?.[i];
      if (!pts || !p) {continue;}
      // Heading on screen from the last few seconds of track (the frame is already axis-up).
      let q: [number, number] | null = null;
      for (let k = i - 3; k >= Math.max(0, i - 8); k--) {if (pts[k]) {q = pts[k]; break;}}
      const angle = q
        ? (Math.atan2(f.sx(p[0]) - f.sx(q[0]), f.sy(q[1]) - f.sy(p[1])) * 180) / Math.PI
        : 0;
      layer.append("path").attr("d", BOAT_PATH)
        .attr("transform", `translate(${f.sx(p[0])},${f.sy(p[1])}) rotate(${angle})`)
        .attr("fill", sourcesStore.getSourceColor(s) || ink.axis)
        .attr("stroke", ink.pointRing).attr("stroke-width", 1.2).attr("stroke-linejoin", "round");
      // Name beside the boat, as on the prestart map.
      layer.append("text").attr("class", "race-course-boat-label")
        .attr("x", f.sx(p[0]) + BOAT_LABEL_DX).attr("y", f.sy(p[1]) + BOAT_LABEL_DY)
        .style("fill", sourcesStore.getSourceColor(s) || ink.label)
        .text(s);
    }
  };

  /** BSP corner table (as on the prestart map): each boat drawn at the cursor, its BSP then. */
  const bspRows = createMemo(() => {
    const i = props.cursor;
    if (i == null) {return [];}
    const pts = frame().pts;
    return props.sources
      .filter((s) => props.race.boats[s] && pts[s]?.[i])
      .map((s) => {
        const kts = props.race.boats[s].bsp_kts[i];
        return {
          name: s,
          color: sourcesStore.getSourceColor(s) || "var(--color-text-secondary)",
          bsp: kts != null && Number.isFinite(kts) ? convertSpeedMagnitude(kts, "kts", props.speedUnit) : null,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  createEffect(() => {
    void props.race; void props.mode; void props.sources; void props.leg; void isDark(); void props.speedUnit;
    frame(); colorScales();
    draw();
  });
  createEffect(() => { void props.cursor; drawCursor(); });
  onCleanup(() => { if (svgRef) {d3.select(svgRef).selectAll("*").remove();} });

  const unit = () => speedUnitShortLabel(props.speedUnit);
  const tws = (kts: number) => convertSpeedMagnitude(kts, "kts", props.speedUnit).toFixed(0);

  return (
    <div class="race-course-view">
      <div class="race-course-frame">
        <svg ref={svgRef} class="race-course-svg" role="img" aria-label={`Race ${props.race.race}${props.leg != null ? ` leg ${props.leg}` : ""} course and tracks`} />
        <Show when={props.cursor != null && bspRows().length > 0}>
          <table class="race-course-bsp-table">
            <caption>{formatRaceClock(raceSecondsAt(props.race, props.cursor ?? 0))}</caption>
            <thead>
              <tr>
                <th>Boat</th>
                <th>BSP {speedUnitBracketUpper(props.speedUnit)}</th>
              </tr>
            </thead>
            <tbody>
              <For each={bspRows()}>
                {(row) => (
                  <tr>
                    <td>
                      <span class="race-course-bsp-swatch" style={{ "background-color": row.color }} />
                      {row.name}
                    </td>
                    <td class="race-course-bsp-value">{row.bsp != null ? row.bsp.toFixed(1) : "–"}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </div>
      <div class="race-chart-legend">
        <Show when={props.mode === "boat"}>
          <For each={props.sources.filter((s) => props.race.boats[s])}>
            {(s) => (
              <span class="race-chart-legend-item">
                <span class="race-chart-legend-swatch" style={{ background: sourcesStore.getSourceColor(s) || "var(--color-text-secondary)" }} />
                {s}
              </span>
            )}
          </For>
        </Show>
        <Show when={props.mode === "wind"}>
          <span class="race-chart-legend-item">Left −{Math.round(colorScales().twdSpread)}°</span>
          <span class="race-chart-legend-ramp race-chart-legend-ramp-wind" />
          <span class="race-chart-legend-item">
            Right +{Math.round(colorScales().twdSpread)}° of mean TWD {Math.round(colorScales().twdMean)}°
          </span>
          <span class="race-chart-legend-note">
            Line width = TWS {tws(colorScales().twsBand[0])}–{tws(colorScales().twsBand[2])} {unit()}
          </span>
        </Show>
        <Show when={props.mode === "dtl"}>
          <span class="race-chart-legend-item">Gaining {Math.round(colorScales().dtlLimit)} m / 10 s</span>
          <span class="race-chart-legend-ramp race-chart-legend-ramp-dtl" />
          <span class="race-chart-legend-item">Losing</span>
          <span class="race-chart-legend-note">Leader is grey (distance to itself is 0)</span>
        </Show>
      </div>
    </div>
  );
}
